import React, { forwardRef, useEffect, useRef, useState } from "react";
import Pagination from "./Pagination";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { getIn } from "formik";
import {
  Checkbox,
  makeStyles,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import Popover from "@material-ui/core/Popover";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import { calcLeftOffset, hideMiddleDigits, isCopyableCodeField, isCopyableCodeTitle, isPatientPhoneField } from "app/common/LocalFunction";
import CopyableCodeCell from "app/common/Component/CopyableCodeCell";
import IconAlignTop from "app/common/Icon/IconAlignTop";
import { PhoneNumberCell } from "utils";

const getColumnWidth = (item) => {
  if (item?.fitContent) return "1%";
  return item?.width;
};

const getColumnWrapStyle = (item) => ({
  wordWrap: item?.fitContent ? "normal" : "break-word",
  whiteSpace: item?.fitContent ? "nowrap" : undefined,
});

const renderTableCellValue = (item, row, index) => {
  if (item?.render) {
    return item.render(row, index);
  }
  const raw = getIn(row, item?.field);
  if (isPatientPhoneField(item?.field)) {
    return <PhoneNumberCell value={raw} />;
  }
  if (isCopyableCodeField(item?.field) || isCopyableCodeTitle(item?.title)) {
    return <CopyableCodeCell value={raw} size="small" />;
  }
  return raw;
};

export const useStylesExtraButton = makeStyles((theme) => ({
  typography: {
    padding: theme.spacing(1),
  },
  rowItem: {
    boxShadow: theme.shadows[1],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(1),
  },
  colItem: {
    display: "flex",
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: "0.85rem",
    color: "#555",
    justifyContent: "space-between",
  },
  labelCol: {
    fontWeight: 600,
    color: "#333",
  },
  valueCol: {
    fontWeight: 500,
    color: "#000",
    wordBreak: "break-word",
    maxWidth: "60%",
  },
  mobileHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  paginationContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: theme.spacing(2),
  },
  selectionInfo: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 500,
  },
  clearSelection: {
    color: theme.palette.error.main,
    cursor: "pointer",
  },
  mobileCheckbox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const TableComponent = ({
  data,
  columns,
  onRowClick,
  titleNodata,
  className,
  dataPagination,
  showOrdinalNumbers,
  finalRow,
  finalRowDetial,
  selectedRow = () => false,
  selectType,
  style,
  maxHeight = "600px",
  minWidth,
  allowHorizontalScroll = false,
  resetSelectedRows,
  initStateRow = [],
  getRowStyle = () => {},
  isPaginationHeader = true,
  rowChild,
  rowEndElement,
  valueCompare = "id",
  doubleClick,
  stylePagination,
  finalDetailRow,
  firstDetailRow,
  chooseRow,
  objectDisable = null,
  name,
  showMoreButton = false,
  rowCss,
  textSingle = "",
  handleChangeSelectedRows,
  scrollContainerRef,
  styleRowHeader = null,
}) => {
  let dataShowTable = JSON.parse(localStorage.getItem("dataShowTable")) || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const classes = useStylesExtraButton();
  const [selectedRows, setSelectedRows] = useState(initStateRow);
  const [showColumnArray, setShowColumnArray] = useState([]);
  const totalColumns = !!selectType
    ? showOrdinalNumbers
      ? columns?.filter((item) => !item?.isHide)?.length + 2
      : columns?.filter((item) => !item?.isHide)?.length + 1
    : showOrdinalNumbers
    ? columns?.filter((item) => !item?.isHide)?.length + 1
    : columns?.filter((item) => !item?.isHide)?.length;
  const align = columns[0]?.align || "left";
  const { t } = useTranslation();

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      let list = data.filter(
        (item) =>
          !selectedRows?.find(
            (sl) => sl?.[valueCompare] === item?.[valueCompare]
          )
      );
      setSelectedRows([...(selectedRows || []), ...list]);
    } else {
      let list = selectedRows.filter(
        (item) =>
          !data?.find((sl) => sl?.[valueCompare] === item?.[valueCompare])
      );
      setSelectedRows(list);
    }
  };

  const handleSelectRow = (row, isChecked) => {
    if (selectType === "multi") {
      if (isChecked) {
        setSelectedRows([...selectedRows, row]);
      } else {
        setSelectedRows(
          selectedRows.filter(
            (selectedRow) => selectedRow?.[valueCompare] !== row?.[valueCompare]
          )
        );
      }
    } else if (selectType === "single") {
      if (isChecked) {
        setSelectedRows([row]);
      } else {
        setSelectedRows([]);
      }
    }
  };

  useEffect(() => {
    if (initStateRow?.length > 0) {
      setSelectedRows(initStateRow);
    }
  }, [initStateRow]);

  const prevSelectedRowsRef = useRef(selectedRows);

  useEffect(() => {
    if (prevSelectedRowsRef.current !== selectedRows) {
      handleChangeSelectedRows(selectedRows);
      prevSelectedRowsRef.current = selectedRows;
    }
  }, [selectedRows]);

  useEffect(() => {
    if (resetSelectedRows && selectedRows !== initStateRow) {
      setSelectedRows(initStateRow);
    }
  }, [resetSelectedRows]);

  useEffect(() => {
    if (columns && showMoreButton) {
      let showColumn = [];

      if (name && dataShowTable[name] && dataShowTable[name]?.length > 0) {
        showColumn = dataShowTable[name];
      } else {
        showColumn = columns?.map((el, index) => ({
          state: el?.isHide ? false : true,
          title: el?.title,
          isFixed: false,
        }));
      }

      setShowColumnArray(showColumn);
    }
  }, []);

  if (!titleNodata) titleNodata = t("noData");
  const isColumnVisible = (index) => {
    return !showMoreButton || showColumnArray?.[index]?.state !== false;
  };

  return (
    <div className={`${className}`}>
      <div className="flex justify-between items-center px-4 py-2 border-b bg-gray-50 font-medium">
        <div>
          {Boolean(isPaginationHeader) &&
          Boolean(dataPagination?.totalElements) ? (
            <div>
              Tổng cộng: <b>{dataPagination.totalElements}</b> Bản ghi
            </div>
          ) : (
            <div style={{ visibility: "hidden" }}>Tổng cộng: 0 Bản ghi</div>
          )}
        </div>

        <div>
          {showMoreButton && (
            <ExtraButton
              name={name}
              setShowColumnArray={setShowColumnArray}
              showColumnArray={showColumnArray}
              dataShowTable={dataShowTable}
              isMobile={isMobile}
            />
          )}
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className={clsx("table-root", allowHorizontalScroll && "table-h-scroll")}
        style={{ overflow: "auto" }}
      >
        <div
          style={{
            maxHeight: maxHeight ? maxHeight : "auto",
            // minWidth: minWidth ? (isMobile ? "auto" : minWidth) : "390px",
          }}
        >
          {isMobile && (
            <div>
              {data?.length > 0 && selectType === "multi" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "5px",
                    position:"sticky",
                    top:"0",
                    backgroundColor:"white"
                  }}
                >
                  <div>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={
                        data?.every((item) =>
                          selectedRows?.some(
                            (sl) => sl?.[valueCompare] === item?.[valueCompare]
                          )
                        ) && data?.length > 0
                      }
                    />
                    <span style={{ marginLeft: 8 }}>{t("Chọn tất cả")}</span>
                  </div>
                </div>
              )}
              {data?.map((row, index) => {
                const isSelected = selectedRows.some(
                  (item) => item?.[valueCompare] === row?.[valueCompare]
                );

                const disabled = objectDisable
                  ? selectedRows.some(
                      (selectedRow) =>
                        selectedRow?.[valueCompare] === row?.[valueCompare] &&
                        selectedRow?.[objectDisable?.field] ===
                          objectDisable?.value
                    )
                  : false;
                return (
                  <div
                    key={index}
                    className={clsx(classes.rowItem, rowCss ? rowCss(row) : "")}
                  >
                    <div className={classes.mobileHeader}>
                      <div className={classes.mobileCheckbox}>
                        {(selectType === "multi" ||
                          selectType === "single") && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={disabled}
                            onChange={(e) => {
                              const isChecked = e.target.checked;

                              if (!disabled) {
                                if (selectType === "single") {
                                  if (isChecked) {
                                    setSelectedRows([row]);
                                  } else {
                                    setSelectedRows([]);
                                  }
                                } else {
                                  handleSelectRow(row, isChecked);
                                }

                                if (onRowClick) onRowClick(row);
                              }
                            }}
                          />
                        )}
                        <b>#{index + 1}</b>
                      </div>
                    </div>
                    <ul>
                      {columns?.map(
                        (item, number) =>
                          isColumnVisible(number) && (
                            <li key={number} className={classes.colItem}>
                              <span className={classes.labelCol}>
                                {item.title}:
                              </span>
                              <span className={classes.valueCol}>
                                {renderTableCellValue(item, row, index)}
                              </span>
                            </li>
                          )
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
          {!isMobile && (
            <div className="table-wrapper">
              <table className="table-container" style={{ ...style }}>
                <thead>
                  <tr
                    className={
                      styleRowHeader != null && styleRowHeader.length > 0
                        ? styleRowHeader
                        : "row-table-header"
                    }
                  >
                    {selectType === "multi" && (
                      <th
                        align="center"
                        width="28px"
                        style={{ boxSizing: "content-box" }}
                      >
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={
                            data?.every((item) =>
                              selectedRows?.find(
                                (sl) =>
                                  sl?.[valueCompare] === item?.[valueCompare]
                              )
                            ) && data?.length > 0
                          }
                        />
                      </th>
                    )}

                    {selectType === "single" && <th>{textSingle}</th>}
                    {showOrdinalNumbers && (
                      <th
                        align={align}
                        width="28px"
                        style={{ boxSizing: "content-box" }}
                      >
                        STT
                      </th>
                    )}
                    {columns.map((item, index) => {
                      if (
                        !showMoreButton ||
                        (showMoreButton && showColumnArray[index]?.state)
                      ) {
                        return (
                          <th
                            key={index}
                            align="center"
                            width={getColumnWidth(item)}
                            style={getColumnWrapStyle(item)}
                            className={
                              showColumnArray[index]?.isFixed
                                ? "fixed-col-left"
                                : ""
                            }
                          >
                            {typeof item?.title === "function"
                              ? item?.title()
                              : item?.title}
                          </th>
                        );
                      }
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data && data.length > 0 && firstDetailRow && firstDetailRow}
                  {data && data.length > 0 ? (
                    <>
                      {data.map((row, index) => (
                        <tr
                          key={index}
                          className={clsx(
                            `row-table-body ${
                              rowCss ? rowCss(row) : ""
                            } pointer row-selection ${
                              chooseRow && chooseRow?.id === row?.id
                                ? "row-choose"
                                : ""
                            }`,
                            (selectedRow(row) ||
                              selectedRows.some(
                                (selectedRow) =>
                                  selectedRow?.[valueCompare] ===
                                  row?.[valueCompare]
                              )) &&
                              "row-selected"
                          )}
                          onDoubleClick={() => {
                            if (doubleClick) {
                              doubleClick(row, index);
                            }
                          }}
                        >
                          {selectType === "multi" && (
                            <td
                              align="center"
                              style={getRowStyle(row)}
                              onClick={() => {
                                const isSelected = selectedRows.some(
                                  (item) =>
                                    item?.[valueCompare] === row?.[valueCompare]
                                );
                                const disabled = objectDisable
                                  ? selectedRows.some(
                                      (selectedRow) =>
                                        selectedRow?.[valueCompare] ===
                                          row?.[valueCompare] &&
                                        selectedRow?.[objectDisable?.field] ===
                                          objectDisable?.value
                                    )
                                  : false;
                                if (!disabled) {
                                  // thêm chức năng disable bên dịch vụ nếu đã là dv trả phí
                                  handleSelectRow(row, !isSelected);
                                  if (onRowClick) {
                                    onRowClick(row);
                                  }
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                className="pointer"
                                checked={selectedRows.some(
                                  (selectedRow) =>
                                    selectedRow?.[valueCompare] ===
                                    row?.[valueCompare]
                                )}
                                onChange={(e) =>
                                  handleSelectRow(row, e?.target?.checked)
                                }
                                disabled={
                                  objectDisable
                                    ? selectedRows.some(
                                        (selectedRow) =>
                                          selectedRow?.[valueCompare] ===
                                            row?.[valueCompare] &&
                                          selectedRow?.[
                                            objectDisable?.field
                                          ] === objectDisable?.value
                                      )
                                    : false
                                }
                              />
                            </td>
                          )}
                          {selectType === "single" && (
                            <td
                              align="center"
                              style={getRowStyle(row)}
                              onClick={() => {
                                const isSelected = selectedRows.some(
                                  (item) =>
                                    item?.[valueCompare] === row?.[valueCompare]
                                );
                                const disabled = objectDisable
                                  ? selectedRows.some(
                                      (selectedRow) =>
                                        selectedRow?.[valueCompare] ===
                                          row?.[valueCompare] &&
                                        selectedRow?.[objectDisable?.field] ===
                                          objectDisable?.value
                                    )
                                  : false;
                                if (!disabled) {
                                  // thêm chức năng disable bên dịch vụ nếu đã là dv trả phí
                                  handleSelectRow(row, !isSelected);
                                  if (onRowClick) {
                                    onRowClick(row);
                                  }
                                }
                              }}
                            >
                              <input
                                type="radio"
                                className="pointer"
                                checked={
                                  selectedRows?.[0]?.[valueCompare] ===
                                  row?.[valueCompare]
                                }
                                onChange={(e) =>
                                  handleSelectRow(row, e?.target?.checked)
                                }
                              />
                            </td>
                          )}
                          {showOrdinalNumbers && (
                            <td align={align} style={getRowStyle(row)}>
                              {index + 1}
                            </td>
                          )}

                          {columns.map((item, number) => {
                            const leftOffset = showColumnArray[number]?.isFixed
                              ? calcLeftOffset(columns, number)
                              : undefined;
                            return !showColumnArray[number]?.state &&
                              showMoreButton ? (
                              <></>
                            ) : (
                              <td
                                key={number}
                                align={item?.align}
                                width={getColumnWidth(item)}
                                style={{
                                  ...getRowStyle(row),
                                  ...getColumnWrapStyle(item),
                                  ...(showColumnArray[number]?.isFixed
                                    ? { left: `${leftOffset}px` }
                                    : {}),
                                }}
                                className={
                                  showColumnArray[number]?.isFixed
                                    ? "fixed-col-left-col"
                                    : ""
                                }
                              >
                                {renderTableCellValue(item, row, index)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {rowChild}
                      {finalRow && (
                        <tr className={"w-100 bg-green-black"}>
                          <td colSpan={totalColumns || 0} align="right">
                            <h5>
                              <strong>{finalRow}</strong>
                            </h5>
                          </td>
                        </tr>
                      )}
                      {finalDetailRow && finalDetailRow}
                    </>
                  ) : (
                    <tr className={"row-table-body"}>
                      <td colSpan={totalColumns || 0} align="center">
                        {titleNodata}
                      </td>
                    </tr>
                  )}

                  {!!rowEndElement && rowEndElement}
                  {data && data.length > 0 && finalRowDetial && finalRowDetial}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <div
        className={`flex-wrap justify-between align-center ${stylePagination}`}
      >
        <div className="flex-wrap align-center gap-1">
          {selectedRows?.length > 0 && selectType === "multi" && (
            <div
              className="flex align-center p-1 gap-1"
              style={{ fontWeight: "500" }}
            >
              <span style={{ flexWrap: "nowrap" }}>
                {" "}
                {selectedRows?.length}
              </span>{" "}
              mục đã chọn
            </div>
          )}
          {selectedRows?.length > 0 && (
            <b
              className="text-danger pointer"
              onClick={() => setSelectedRows([])}
            >
              <i>(Bỏ chọn)</i>
            </b>
          )}
        </div>
        {Boolean(dataPagination) && <Pagination {...dataPagination} />}
      </div>
    </div>
  );
};

const ExtraButton = ({
  name,
  setShowColumnArray,
  showColumnArray,
  dataShowTable,
  isMobile,
}) => {
  const classes = useStylesExtraButton();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const id = !!anchorEl ? "simple-popover" : undefined;
  const onToggle = (index, state) => {
    setShowColumnArray((prev) => {
      let newArr = prev?.map((el, ind) => {
        if (ind === index) return { ...el, state };
        else return el;
      });
      dataShowTable[name] = newArr;

      localStorage.setItem("dataShowTable", JSON.stringify(dataShowTable));
      return newArr;
    });
  };

  const onToggleFixed = (index, isFixed) => {
    setShowColumnArray((prev) => {
      let newArr = prev?.map((el, ind) => ({
        ...el,
        isFixed: ind === index ? isFixed : false,
      }));
      dataShowTable[name] = newArr;

      localStorage.setItem("dataShowTable", JSON.stringify(dataShowTable));
      return newArr;
    });
  };

  return (
    <div>
      <Button
        aria-describedby={id}
        variant="contained"
        color="primary"
        onClick={handleClick}
        className="extra-button-table"
      >
        <div className="flex align-center justify-between">
          {!isMobile && "Xem thêm"}
          <Tooltip title="Xem thêm">
            {!!anchorEl ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
          </Tooltip>
        </div>
      </Button>
      <Popover
        id={id}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Typography className={classes.typography}>
          {showColumnArray &&
            showColumnArray?.map((el, ind) => (
              <CustomToggle
                label={el?.title}
                onToggle={onToggle}
                onToggleFixed={onToggleFixed}
                defaultValue={el?.state}
                defaultFixed={el?.isFixed}
                index={ind}
              />
            ))}

          {/* xin chao cac ban */}
        </Typography>
      </Popover>
    </div>
  );
};

const CustomToggle = ({
  label,
  defaultValue = false,
  defaultFixed = false,
  onToggle,
  onToggleFixed,
  index,
}) => {
  const [active, setActive] = useState(defaultValue);
  const [activeFixed, setActiveFixed] = useState(defaultFixed);

  const handleClick = (index) => {
    setActive((prev) => !prev);
    if (onToggle) {
      onToggle(index, !active);
    }
  };

  const handleFixed = (index) => {
    setActiveFixed((prev) => !prev);
    if (onToggleFixed) {
      onToggleFixed(index, !activeFixed);
    }
  };

  return (
    <div className="flex gap-2 align-center toggle-wraper">
      <div className="toggle-container">
        <div
          className={`custom-toggle ${active ? "active" : ""}`}
          onClick={() => handleClick(index)}
        >
          <div className="toggle-circle" />
        </div>
        <div className="flex justify-between align-center" style={{ flex: 1 }}>
          <span className="toggle-label">{label}</span>
          <Tooltip title="Cố định cột">
            <div
              className={`align-center fixed-btn ${
                activeFixed ? "visible-true" : "visible-none"
              } `}
              onClick={() => handleFixed(index)}
            >
              <IconAlignTop color="#3699FF" />
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default TableComponent;
